import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { submitToGoogleSheets } from '../../services/googleSheets';
import { POSITION_CONFIGS, normalizePositionTitle } from '../../config/positionConfigs';

export function JobApplicationForm({ position }) {
  const { positionSlug } = useParams();
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Default position data if not provided through props
  const defaultPosition = {
    title: positionSlug ? normalizePositionTitle(
      positionSlug.split('-').map(word => {
        if (word === 'fullstack') return 'Full Stack';
        if (word === 'ux' || word === 'ui') return word.toUpperCase();
        return word.charAt(0).toUpperCase() + word.slice(1);
      }).join(' ') + (positionSlug.includes('engineer') && !positionSlug.endsWith('engineer') ? ' Engineer' : '')
    ) : 'Position',
  };

  // Use provided position or default
  const currentPosition = position || defaultPosition;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: currentPosition.title,
    techExpertise: '',
    aiExperience: '',
    portfolio: '',
    englishFluency: '',
    noticePeriod: '',
    resumeLink: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      setFormData(prev => ({
        ...prev,
        [name]: files[0]
      }));
    }
  };

  // Update the portfolio/github validation and display logic
  const getLinkRequirement = (position) => {
    const positionLower = position.toLowerCase();
    if (positionLower.includes('frontend') || positionLower.includes('ux') || positionLower.includes('ui')) {
      return { required: true, type: 'Portfolio' };
    } else if (positionLower.includes('backend')) {
      return { required: true, type: 'GitHub' };
    }
    return { required: false, type: 'Portfolio/GitHub' };
  };

  // Update the getPositionQuestions function
  const getPositionQuestions = (position) => {
    return POSITION_CONFIGS[position]?.questions || {
      techQuestion: 'Please describe your technical expertise',
      aiQuestion: 'Do you have experience with AI features?',
      linkType: 'Portfolio/GitHub'
    };
  };

  // Add position-specific form fields
  const getAdditionalFields = (position) => {
    const config = POSITION_CONFIGS[position];
    if (!config || !config.additionalFields) return [];

    return config.additionalFields.map(field => {
      const question = config.questions[field + 'Question'];
      return (
        <div key={field}>
          <label htmlFor={field} className="block text-sm font-medium text-gray-700">
            {question} *
          </label>
          <textarea
            name={field}
            id={field}
            required
            rows={3}
            value={formData[field] || ''}
            onChange={handleInputChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#0049ac] focus:ring-[#0049ac]"
            placeholder="Please describe your experience..."
          />
        </div>
      );
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Form validation
    if (!formData.name || !formData.email || !formData.phone || !formData.noticePeriod) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Phone validation
    const phoneRegex = /^\+?[\d\s-]{8,}$/;
    if (!phoneRegex.test(formData.phone)) {
      toast.error('Please enter a valid phone number');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Notice period validation
    if (!formData.noticePeriod) {
      toast.error('Please provide your notice period');
      return;
    }

    // Portfolio/GitHub validation
    const linkRequirement = getLinkRequirement(currentPosition.title);
    if (linkRequirement.required && !formData.portfolio) {
      toast.error(`${linkRequirement.type} link is required for this position`);
      return;
    }

    try {
      const loadingToast = toast.loading('Submitting application...');
      
      const response = await submitToGoogleSheets(formData);
      
      toast.dismiss(loadingToast);

      if (response.status === 'success') {
        // Clear form
        setFormData({
          name: '',
          email: '',
          phone: '',
          position: currentPosition.title,
          techExpertise: '',
          aiExperience: '',
          portfolio: '',
          englishFluency: '',
          noticePeriod: '',
          resumeLink: '',
        });

        toast.success(response.message);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to submit application. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <Link
            to="/careers"
            className="inline-flex items-center text-sm font-medium text-[#0049ac] hover:text-[#0049ac]/90"
          >
            ← Back to Careers
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-xl overflow-hidden">
          <div className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Apply for {currentPosition.title}</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">Name *</label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#0049ac] focus:ring-[#0049ac]"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email *</label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#0049ac] focus:ring-[#0049ac]"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    id="phone"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="e.g., +1 234 567 8900"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#0049ac] focus:ring-[#0049ac]"
                  />
                </div>

                <div>
                  <label htmlFor="techExpertise" className="block text-sm font-medium text-gray-700">
                    {getPositionQuestions(currentPosition.title).techQuestion} *
                  </label>
                  <textarea
                    name="techExpertise"
                    id="techExpertise"
                    required
                    rows={3}
                    value={formData.techExpertise}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#0049ac] focus:ring-[#0049ac]"
                    placeholder="Please describe your experience..."
                  />
                </div>

                {/* Only render AI Experience question if it exists for the position */}
                {getPositionQuestions(currentPosition.title).aiQuestion && (
                  <div>
                    <label htmlFor="aiExperience" className="block text-sm font-medium text-gray-700">
                      {getPositionQuestions(currentPosition.title).aiQuestion} *
                    </label>
                    <textarea
                      name="aiExperience"
                      id="aiExperience"
                      required
                      rows={3}
                      value={formData.aiExperience}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#0049ac] focus:ring-[#0049ac]"
                      placeholder="Please describe your experience..."
                    />
                  </div>
                )}

                {getAdditionalFields(currentPosition.title)}

                <div>
                  <label htmlFor="portfolio" className="block text-sm font-medium text-gray-700">
                    Please add a link to your {getPositionQuestions(currentPosition.title).linkType} here: *
                  </label>
                  <input
                    type="url"
                    name="portfolio"
                    id="portfolio"
                    required={getLinkRequirement(currentPosition.title).required}
                    value={formData.portfolio}
                    onChange={handleInputChange}
                    placeholder={`https://${getPositionQuestions(currentPosition.title).linkType.toLowerCase() === 'github' ? 'github.com/your-username' : 'your-portfolio.com'}`}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#0049ac] focus:ring-[#0049ac]"
                  />
                </div>

                <div>
                  <label htmlFor="englishFluency" className="block text-sm font-medium text-gray-700">What is your English proficiency level? *</label>
                  <select
                    name="englishFluency"
                    id="englishFluency"
                    required
                    value={formData.englishFluency}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#0049ac] focus:ring-[#0049ac]"
                  >
                    <option value="">Select Level</option>
                    <option value="Native">Native</option>
                    <option value="Fluent">Fluent</option>
                    <option value="Advanced">Advanced</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Basic">Basic</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="noticePeriod" className="block text-sm font-medium text-gray-700">What is your notice period? *</label>
                  <div className="mt-1 text-sm text-gray-500 mb-2">
                    Note: We prefer candidates who can start immediately.
                  </div>
                  <input
                    type="text"
                    name="noticePeriod"
                    id="noticePeriod"
                    required
                    value={formData.noticePeriod}
                    onChange={handleInputChange}
                    placeholder="e.g., Immediately, 1 week, 2 weeks"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#0049ac] focus:ring-[#0049ac]"
                  />
                </div>

                <div>
  <div className="mb-2">
    <label htmlFor="resumeLink" className="block text-sm font-medium text-gray-700">
      Resume/CV Link *
    </label>
    <div className="mt-1 text-sm text-gray-500 mb-2">
      Please upload your resume to Google Drive and provide the shareable link below. Make sure the link is accessible to anyone with the link.
    </div>
  </div>
  <input
    type="url"
    name="resumeLink"
    id="resumeLink"
    required
    value={formData.resumeLink}
    onChange={handleInputChange}
    placeholder="https://drive.google.com/file/d/..."
    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#0049ac] focus:ring-[#0049ac]"
  />
</div>
              </div>

              <div>
                <button
                  type="submit"
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#0049ac] hover:bg-[#0049ac]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0049ac]"
                >
                  Submit Application
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default JobApplicationForm; 